<template>
  <div
    v-if="value && useRedesign"
    class="inspection-step-dialog-overlay"
    @click.self="handleOverlayClick"
  >
    <div
      ref="dialogRef"
      class="isd"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inspection-step-dialog-title"
    >
      <div class="isd__header">
        <img
          :src="require('@components/assets/search-refraction.svg')"
          alt=""
          class="isd__header-icon"
        />
        <h2 id="inspection-step-dialog-title" class="isd__title">{{ dialogTitle }}</h2>
        <button
          type="button"
          class="isd__close"
          :disabled="isBusy"
          aria-label="Close dialog"
          @click="handleClose"
        >
          <img :src="require('@components/assets/x-close.svg')" alt="" />
        </button>
      </div>

      <div class="isd__body">
        <div class="isd__group">
          <span id="inspection-step-type-label" class="isd__label">Type</span>
          <div
            class="isd__type"
            role="radiogroup"
            aria-labelledby="inspection-step-type-label"
            @keydown.left.prevent="moveCategory(-1)"
            @keydown.right.prevent="moveCategory(1)"
          >
            <button
              v-for="option in categoryOptions"
              :key="option.value"
              type="button"
              role="radio"
              class="isd__type-option"
              :class="{ 'isd__type-option--selected': localCategory === option.value }"
              :aria-checked="localCategory === option.value ? 'true' : 'false'"
              :tabindex="localCategory === option.value ? 0 : -1"
              @click="handleCategoryChange(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="isd__fields">
          <div
            v-for="field in fieldModels"
            :key="field.key"
            class="isd__field"
            :class="[`isd__field--${field.tone}`, { 'isd__field--invalid': field.error }]"
          >
            <label class="isd__label" :class="`isd__label--${field.tone}`" :for="field.id">
              {{ field.label }}
            </label>
            <textarea
              :id="field.id"
              class="isd__input"
              rows="3"
              :value="field.value"
              :placeholder="field.placeholder"
              :maxlength="MAX_LENGTH"
              :aria-invalid="field.error ? 'true' : 'false'"
              :aria-describedby="field.describedBy"
              @input="setFieldValue(field.key, $event.target.value)"
              @blur="markTouched(field.key)"
            ></textarea>
            <div class="isd__meta">
              <p v-if="field.error" :id="field.errorId" class="isd__error" role="alert">
                {{ field.error }}
              </p>
              <span v-else class="isd__meta-spacer"></span>
              <span :id="field.counterId" class="isd__counter">
                {{ field.length }}/{{ MAX_LENGTH }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="timestampEnabled"
          class="isd__group isd__timestamp"
          role="group"
          aria-labelledby="inspection-step-timestamp-label"
        >
          <span id="inspection-step-timestamp-label" class="isd__label">
            Video timestamp (optional)
          </span>
          <div class="isd__timestamp-row">
            <input
              v-model="localTimestampStart"
              type="text"
              class="isd__input isd__input--time"
              placeholder="00:00"
              maxlength="8"
              inputmode="numeric"
              aria-label="Start time (mm:ss)"
              :aria-invalid="timestampErrorVisible ? 'true' : 'false'"
              :aria-describedby="timestampDescribedBy"
              @input="emitChange"
              @blur="timestampTouched = true"
            />
            <span class="isd__timestamp-to">to</span>
            <input
              v-model="localTimestampEnd"
              type="text"
              class="isd__input isd__input--time"
              placeholder="1:00"
              maxlength="8"
              inputmode="numeric"
              aria-label="End time (mm:ss)"
              :aria-invalid="timestampErrorVisible ? 'true' : 'false'"
              :aria-describedby="timestampDescribedBy"
              @input="emitChange"
              @blur="timestampTouched = true"
            />
          </div>
          <p v-if="timestampErrorVisible" id="inspection-step-timestamp-error" class="isd__error" role="alert">
            {{ timestampError }}
          </p>
          <p id="inspection-step-timestamp-hint" class="isd__hint">
            Inspectors see this as a Play button on the step.
          </p>
        </div>
      </div>

      <div class="isd__footer">
        <StandardButton
          v-if="isEditMode"
          type="red"
          variant="outline"
          size="normal"
          label="Delete"
          :loader-left="isDeleting"
          :disabled="isBusy"
          @click="handleDelete"
        />
        <span class="isd__footer-spacer"></span>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Discard"
          :disabled="isBusy"
          @click="handleDiscard"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="normal"
          label="Save"
          :loader-left="isSubmitting"
          :disabled="!canSubmit || isBusy"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>

  <div
    v-else-if="value"
    class="inspection-step-dialog-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="inspection-step-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <img :src="require('@components/assets/search-refraction.svg')" alt="inspection" class="header-icon-img" />
          <h2 class="header-title">Inspection Step</h2>
        </div>
        <button
          type="button"
          class="close-button"
          :disabled="isSubmitting || isDeleting"
          aria-label="Close dialog"
          @click="handleClose"
        >
          <img :src="require('@components/assets/x-close.svg')" alt="close" />
        </button>
      </div>

      <!-- Dialog Content -->
      <div class="dialog-content">
        <!-- Action Bar -->
        <div class="action-bar">
          <div class="action-bar-row">
            <!-- Inspect Label and Category Picker -->
            <div class="inspect-section">
              <span class="inspect-label">Inspect</span>
              <CompetenceCategoryPicker
                :modelValue="localCategory"
                @update:modelValue="handleCategoryChange"
              />
            </div>

            <!-- Spacer -->
            <div class="action-spacer"></div>

            <!-- Buttons -->
            <div class="action-buttons">
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label="Discard"
                :disabled="isSubmitting || isDeleting"
                @click="handleDiscard"
              />
              <StandardButton
                type="magenta"
                variant="primary"
                size="normal"
                :label="isEditMode ? 'Save' : 'Add'"
                :left-icon="isEditMode || isSubmitting || isDeleting ? undefined : 'plusLeft'"
                :loader-left="isSubmitting"
                :disabled="!canSubmit || isSubmitting || isDeleting"
                @click="handleSubmit"
              />
              <StandardButton
                v-if="isEditMode"
                type="red"
                variant="outline"
                size="normal"
                label="Delete"
                :loader-left="isDeleting"
                :disabled="isSubmitting || isDeleting"
                @click="handleDelete"
              />
            </div>
          </div>
        </div>

        <!-- Form Fields -->
        <div class="form-fields">
          <div class="form-field">
            <ExTextInputWithLimit
              :value="localInspect"
              label="Inspect"
              :placeholder="'What do you want to inspect'"
              :ghost-text="'What do you want to inspect'"
              :status="getFieldStatus('inspect')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleInspectChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localLookFor"
              label="Look for"
              :placeholder="'What are you looking for'"
              :ghost-text="'What are you looking for'"
              :status="getFieldStatus('lookFor')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleLookForChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localPassIf"
              label="Pass Inspection if"
              :placeholder="'Describe pass'"
              :ghost-text="'Describe pass'"
              :status="getFieldStatus('passIf')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handlePassIfChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localFailIf"
              label="Fail Inspection if"
              :placeholder="'Describe failure'"
              :ghost-text="'Describe failure'"
              :status="getFieldStatus('failIf')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleFailIfChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import { LD_FLAG_IDEA_MDP_452 } from '@components/constants/launchDarklyFlagKeys';
import CompetenceCategoryPicker from '@components/CompetenceCategoryPicker/CompetenceCategoryPicker.vue';
import ExTextInputWithLimit from '@components/TextInputWithLimit/ExTextInputWithLimit.vue';
import StandardButton from '@components/StandardButton/StandardButton.vue';

const props = defineProps({
  /**
   * Controls dialog visibility (v-model)
   */
  value: {
    type: Boolean,
    default: false
  },
  /**
   * Dialog state: Empty (new), Add (filled new), Edit (existing unchanged), Save (existing changed)
   */
  state: {
    type: String,
    default: 'Empty',
    validator: (value) => ['Empty', 'Add', 'Edit', 'Save'].includes(value)
  },
  /**
   * Initial category value
   */
  category: {
    type: String,
    default: 'knowledge',
    validator: (value) => ['knowledge', 'ability', 'behaviour'].includes(value)
  },
  /**
   * Initial inspect value
   */
  inspect: {
    type: String,
    default: ''
  },
  /**
   * Initial look for value
   */
  lookFor: {
    type: String,
    default: ''
  },
  /**
   * Initial pass if value
   */
  passIf: {
    type: String,
    default: ''
  },
  /**
   * Initial fail if value
   */
  failIf: {
    type: String,
    default: ''
  },
  /**
   * Whether to close on overlay click
   */
  persistent: {
    type: Boolean,
    default: false
  },
  /**
   * Whether to call API on save (if false, just emit submit event)
   */
  callApiOnSave: {
    type: Boolean,
    default: false
  },
  /**
   * When true: primary action shows a loader, dismiss controls are disabled, and overlay/escape do not close the dialog.
   */
  isSubmitting: {
    type: Boolean,
    default: false
  },
  /**
   * When true: delete action shows a loader; other actions are disabled until the parent clears this.
   */
  isDeleting: {
    type: Boolean,
    default: false
  },
  /**
   * Initial video timestamp as "start - end" (mm:ss). Only used when showTimestamp is true.
   */
  timestampRef: {
    type: String,
    default: ''
  },
  /**
   * Show the Video timestamp group (video checkpoints only). Redesigned dialog only.
   */
  showTimestamp: {
    type: Boolean,
    default: false
  },
  /**
   * Feature flag `idea-MDP-452` (New Checkpoint Edit and Inspection). `null` resolves the
   * LaunchDarkly flag; a boolean overrides it (Storybook, tests). Off renders the legacy dialog.
   */
  ideaMDP452: {
    type: Boolean,
    default: null
  }
});

const emit = defineEmits([
  'input',
  'close',
  'discard',
  'submit',
  'delete',
  'change'
]);

const MAX_LENGTH = 120;
const MIN_LENGTH = 3;

const categoryOptions = [
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'ability', label: 'Ability' },
  { value: 'behaviour', label: 'Behavior' }
];

const { proxy } = getCurrentInstance();
const flagEnabled = ref(false);
const useRedesign = computed(() => (props.ideaMDP452 === null ? flagEnabled.value : props.ideaMDP452));

onMounted(async () => {
  if (props.ideaMDP452 !== null) return;
  try {
    flagEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_452, false)) === true;
  } catch {
    flagEnabled.value = false;
  }
});

const dialogRef = ref(null);

// Split "start - end" into its two clock values; a lone value is kept as the start.
const splitTimestamp = (ref) => {
  const [start = '', end = ''] = String(ref || '').split(/\s*[-\u2013]\s*/);
  return { start: start.trim(), end: end.trim() };
};

// Local state
const initialTimestamp = splitTimestamp(props.timestampRef);
const localTimestampStart = ref(initialTimestamp.start);
const localTimestampEnd = ref(initialTimestamp.end);
const touched = ref({});
const timestampTouched = ref(false);
const submitAttempted = ref(false);
const localCategory = ref(props.category);
const localInspect = ref(props.inspect);
const localLookFor = ref(props.lookFor);
const localPassIf = ref(props.passIf);
const localFailIf = ref(props.failIf);

// Track original values for dirty checking
const originalValues = ref({
  category: props.category,
  inspect: props.inspect,
  lookFor: props.lookFor,
  passIf: props.passIf,
  failIf: props.failIf,
  timestampStart: initialTimestamp.start,
  timestampEnd: initialTimestamp.end
});

// Computed properties
const isBusy = computed(() => props.isSubmitting || props.isDeleting);

const timestampEnabled = computed(() => useRedesign.value && props.showTimestamp);

const isDirty = computed(() => {
  return (
    localCategory.value !== originalValues.value.category ||
    (timestampEnabled.value &&
      (localTimestampStart.value.trim() !== originalValues.value.timestampStart ||
        localTimestampEnd.value.trim() !== originalValues.value.timestampEnd)) ||
    localInspect.value !== originalValues.value.inspect ||
    localLookFor.value !== originalValues.value.lookFor ||
    localPassIf.value !== originalValues.value.passIf ||
    localFailIf.value !== originalValues.value.failIf
  );
});

const isFormValid = computed(() => {
  const inspectValid = localInspect.value && localInspect.value.trim().length >= 3;
  const lookForValid = localLookFor.value && localLookFor.value.trim().length >= 3;
  const passIfValid = localPassIf.value && localPassIf.value.trim().length >= 3;
  const failIfValid = localFailIf.value && localFailIf.value.trim().length >= 3;
  
  return inspectValid && lookForValid && passIfValid && failIfValid && !timestampError.value;
});

// Check if we're in edit mode (editing existing data)
const isEditMode = computed(() => {
  return props.state === 'Edit' || props.state === 'Save';
});

// Can submit when form is valid AND has changes
const canSubmit = computed(() => {
  return isFormValid.value && isDirty.value;
});

const clockToSeconds = (value) => {
  const match = /^(?:(\d{1,2}):)?(\d{1,3}):([0-5]\d)$/.exec(value);
  if (!match) return null;
  const [, hours = '0', minutes, seconds] = match;
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

// Untouched start/end are never re-validated so a legacy value always round-trips.
const timestampError = computed(() => {
  if (!timestampEnabled.value) return '';
  const start = localTimestampStart.value.trim();
  const end = localTimestampEnd.value.trim();
  if (start === originalValues.value.timestampStart && end === originalValues.value.timestampEnd) return '';
  if (!start && !end) return '';
  if (!start || !end) return 'Enter both a start and an end time';
  const startSeconds = clockToSeconds(start);
  const endSeconds = clockToSeconds(end);
  if (startSeconds === null || endSeconds === null) return 'Use the mm:ss format, for example 01:30';
  if (endSeconds <= startSeconds) return 'End time must be after the start time';
  return '';
});

const timestampErrorVisible = computed(() => {
  if (!timestampError.value) return false;
  const bothFilled = Boolean(localTimestampStart.value.trim() && localTimestampEnd.value.trim());
  return bothFilled || timestampTouched.value || submitAttempted.value;
});

const timestampDescribedBy = computed(() =>
  timestampErrorVisible.value
    ? 'inspection-step-timestamp-error inspection-step-timestamp-hint'
    : 'inspection-step-timestamp-hint'
);

const dialogTitle = computed(() => (isEditMode.value ? 'Edit inspection step' : 'Add inspection step'));

const fieldDefs = [
  { key: 'inspect', label: 'What to inspect', placeholder: 'What should the inspector observe?', tone: 'neutral' },
  { key: 'lookFor', label: 'Look for', placeholder: 'What exactly should they look for?', tone: 'neutral' },
  { key: 'passIf', label: 'Pass criteria', placeholder: 'Describe what passing looks like', tone: 'success' },
  { key: 'failIf', label: 'Fail criteria', placeholder: 'Describe what failing looks like', tone: 'error' }
];

const fieldValues = { inspect: localInspect, lookFor: localLookFor, passIf: localPassIf, failIf: localFailIf };

const fieldModels = computed(() =>
  fieldDefs.map((def) => {
    const value = fieldValues[def.key].value || '';
    const trimmedLength = value.trim().length;
    let error = '';
    if (touched.value[def.key] || submitAttempted.value) {
      if (trimmedLength === 0) error = 'Required';
      else if (trimmedLength < MIN_LENGTH) error = `Enter at least ${MIN_LENGTH} characters`;
    }
    const id = `inspection-step-${def.key}`;
    return {
      ...def,
      id,
      errorId: `${id}-error`,
      counterId: `${id}-counter`,
      value,
      length: value.length,
      error,
      describedBy: error ? `${id}-error ${id}-counter` : `${id}-counter`
    };
  })
);

// Methods
const getFieldStatus = (fieldName) => {
  const fieldMap = {
    inspect: { value: localInspect.value, original: originalValues.value.inspect },
    lookFor: { value: localLookFor.value, original: originalValues.value.lookFor },
    passIf: { value: localPassIf.value, original: originalValues.value.passIf },
    failIf: { value: localFailIf.value, original: originalValues.value.failIf }
  };
  
  const field = fieldMap[fieldName];
  if (!field) return 'Not focused';
  
  // Check if field has been modified from original
  if (field.value !== field.original && field.value && field.value.length > 0) {
    return 'Dirty';
  }
  
  return 'Not focused';
};

const handleInspectChange = (value) => {
  localInspect.value = value;
  emitChange();
};

const handleLookForChange = (value) => {
  localLookFor.value = value;
  emitChange();
};

const handlePassIfChange = (value) => {
  localPassIf.value = value;
  emitChange();
};

const handleFailIfChange = (value) => {
  localFailIf.value = value;
  emitChange();
};

const handleCategoryChange = (value) => {
  localCategory.value = value;
  emitChange();
};

const fieldSetters = {
  inspect: handleInspectChange,
  lookFor: handleLookForChange,
  passIf: handlePassIfChange,
  failIf: handleFailIfChange
};

const setFieldValue = (key, value) => fieldSetters[key](value);

const markTouched = (key) => {
  touched.value = { ...touched.value, [key]: true };
};

const moveCategory = (step) => {
  const index = categoryOptions.findIndex((option) => option.value === localCategory.value);
  const next = categoryOptions[(index + step + categoryOptions.length) % categoryOptions.length];
  handleCategoryChange(next.value);
  nextTick(() => dialogRef.value?.querySelector('[role="radio"][aria-checked="true"]')?.focus());
};

const buildTimestampRef = () => {
  const start = localTimestampStart.value.trim();
  const end = localTimestampEnd.value.trim();
  return start && end ? `${start} - ${end}` : start;
};

const emitChange = () => {
  emit('change', {
    category: localCategory.value,
    inspect: localInspect.value,
    lookFor: localLookFor.value,
    passIf: localPassIf.value,
    failIf: localFailIf.value,
    ...(timestampEnabled.value && { timestamp_ref: buildTimestampRef() }),
    isDirty: isDirty.value,
    isValid: isFormValid.value
  });
};

const handleClose = () => {
  if (props.isSubmitting || props.isDeleting) return;
  emit('input', false);
  emit('close');
};

const handleOverlayClick = () => {
  if (props.persistent || props.isSubmitting || props.isDeleting) return;
  handleClose();
};

const handleDiscard = () => {
  if (props.isSubmitting || props.isDeleting) return;
  // Reset to original values
  localCategory.value = originalValues.value.category;
  localInspect.value = originalValues.value.inspect;
  localLookFor.value = originalValues.value.lookFor;
  localPassIf.value = originalValues.value.passIf;
  localFailIf.value = originalValues.value.failIf;
  localTimestampStart.value = originalValues.value.timestampStart;
  localTimestampEnd.value = originalValues.value.timestampEnd;

  emit('discard');
};

const handleSubmit = () => {
  submitAttempted.value = true;
  if (!isFormValid.value || props.isSubmitting || props.isDeleting) return;

  emit('submit', {
    category: localCategory.value,
    inspect: localInspect.value,
    lookFor: localLookFor.value,
    passIf: localPassIf.value,
    failIf: localFailIf.value,
    ...(timestampEnabled.value && { timestamp_ref: buildTimestampRef() })
  });
};

const handleDelete = () => {
  if (props.isSubmitting || props.isDeleting) return;
  emit('delete');
};

// Handle escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.value && !props.persistent && !props.isSubmitting && !props.isDeleting) {
    handleClose();
  }
};

// Watchers
watch(() => props.value, (newVal) => {
  if (newVal) {
    // Reset local state when dialog opens
    localCategory.value = props.category;
    localInspect.value = props.inspect;
    localLookFor.value = props.lookFor;
    localPassIf.value = props.passIf;
    localFailIf.value = props.failIf;

    const timestamp = splitTimestamp(props.timestampRef);
    localTimestampStart.value = timestamp.start;
    localTimestampEnd.value = timestamp.end;
    touched.value = {};
    timestampTouched.value = false;
    submitAttempted.value = false;

    // Update original values
    originalValues.value = {
      category: props.category,
      inspect: props.inspect,
      lookFor: props.lookFor,
      passIf: props.passIf,
      failIf: props.failIf,
      timestampStart: timestamp.start,
      timestampEnd: timestamp.end
    };

    nextTick(() => dialogRef.value?.querySelector('textarea')?.focus());
    
    // Add escape key listener (remove first to prevent duplicates)
    document.removeEventListener('keydown', handleKeydown);
    document.addEventListener('keydown', handleKeydown);
  } else {
    document.removeEventListener('keydown', handleKeydown);
  }
}, { immediate: true });

// Lifecycle
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.inspection-step-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(52, 64, 84, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.inspection-step-dialog {
  position: relative;
  width: 1031px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  background-color: #ffffff;
  background-image: url('@components/assets/backgrounds/background_popup_icon.svg');
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 12px;
  box-shadow: 
    0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: visible;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 20px 24px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-icon-img {
  width: 48px;
  height: 48px;
}

.header-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.close-button:hover {
  background-color: #f9fafb;
}

.close-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.close-button img {
  width: 24px;
  height: 24px;
}

/* Dialog Content */
.dialog-content {
  display: flex;
  flex-direction: column;
}

/* Action Bar */
.action-bar {
  border: 1px solid #e4e4e4;
  padding: 19px 13px;
}

.action-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inspect-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inspect-label {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 30px;
  color: #475467;
}

.action-spacer {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

/* Form Fields */
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 19px 13px;
  border: 1px solid #e4e4e4;
  border-top: none;
}

.form-field {
  width: 100%;
  margin-top: 10px;
}

/* Override ExTextInputWithLimit label styles for this dialog */
.form-field :deep(.main_label) {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

/* Redesigned dialog (idea-MDP-452) */
.isd {
  position: relative;
  width: 720px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow:
    0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: hidden;
  font-family: Inter, sans-serif;
}

.isd__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 16px 24px;
}

.isd__header-icon {
  width: 40px;
  height: 40px;
}

.isd__title {
  flex: 1;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.isd__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
}

.isd__close:hover {
  background-color: #f9fafb;
}

.isd__close:focus-visible,
.isd__type-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.isd__close:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.isd__close img {
  width: 24px;
  height: 24px;
}

.isd__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  overflow-y: auto;
}

.isd__group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.isd__label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.isd__label--success {
  color: #067647;
}

.isd__label--error {
  color: #b42318;
}

.isd__type {
  display: flex;
  align-self: flex-start;
  padding: 4px;
  border-radius: 8px;
  background-color: #f2f4f7;
}

.isd__type-option {
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  cursor: pointer;
}

.isd__type-option--selected {
  background-color: #ffffff;
  font-weight: 600;
  color: #101828;
}

.isd__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 12px;
}

.isd__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: span 2;
  min-width: 0;
}

.isd__field--success,
.isd__field--error {
  grid-column: span 1;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 8px;
}

.isd__field--success {
  background-color: #ecfdf3;
  border-color: #abefc6;
}

.isd__field--error {
  background-color: #fef3f2;
  border-color: #fecdca;
}

.isd__input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background-color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
  resize: none;
}

.isd__input::placeholder {
  color: #475467;
}

.isd__input:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 0;
}

.isd__field--invalid .isd__input {
  border-color: #b42318;
}

.isd__meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.isd__meta-spacer {
  flex: 1;
}

.isd__error {
  flex: 1;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
}

.isd__counter {
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.isd__hint {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.isd__timestamp-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.isd__input--time {
  width: 120px;
}

.isd__timestamp-to {
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.isd__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background-color: #f9fafb;
}

.isd__footer-spacer {
  flex: 1;
}
</style>
